import { themeConfig } from "../theme.config";
import { contrastRatio, generateScale, type Rgb, SCALE_STOPS, type Shade } from "./palette";

const WHITE: Rgb = [255, 255, 255];

// Candidate shades for the "action" slot, ordered by visual preference:
//  - Light mode: start near the base (shade 500) and walk darker until 4.5:1 against white passes.
//  - Dark mode: start lighter than base (shade 400) and walk lighter until 4.5:1 against the
//    darkest scale shade passes.
// Adaptive picking handles all base lightnesses — from near-black brand to vivid mid-L colors.
const LIGHT_CANDIDATES: Shade[] = [500, 600, 700, 800, 900, 950];
const DARK_CANDIDATES: Shade[] = [400, 300, 200, 100, 50];

const triplet = ([r, g, b]: Rgb) => `${r} ${g} ${b}`;

/**
 * Compute primary + accent scales from theme.config and write them to :root as
 * space-separated RGB triplets (so Tailwind's `<alpha-value>` and class modifiers
 * like `accent-500/30` keep working). Also picks contrast-safe `--primary` /
 * `--primary-fg` aliases per theme and injects them as a stylesheet.
 */
export function applyPalette(): void {
  const root = document.documentElement;
  const primary = generateScale(themeConfig.primaryBase);
  const accent = generateScale(themeConfig.accentBase);

  for (const s of SCALE_STOPS) {
    root.style.setProperty(`--primary-${s}`, triplet(primary[s]));
    root.style.setProperty(`--accent-${s}`, triplet(accent[s]));
  }

  const lightShade = pickActionShade(primary, WHITE, LIGHT_CANDIDATES);
  const darkShade = pickActionShade(primary, primary[950], DARK_CANDIDATES);
  injectActionAliases(lightShade, darkShade);

  if (import.meta.env.DEV) {
    assertContrast("light", primary[lightShade], WHITE);
    assertContrast("dark", primary[darkShade], primary[950]);
  }
}

function pickActionShade(scale: Record<Shade, Rgb>, fg: Rgb, candidates: Shade[]): Shade {
  for (const shade of candidates) {
    if (contrastRatio(scale[shade], fg) >= 4.5) return shade;
  }
  // Best-effort fallback: highest-contrast candidate. assertContrast will warn.
  return candidates.reduce((best, s) =>
    contrastRatio(scale[s], fg) > contrastRatio(scale[best], fg) ? s : best,
  );
}

const STYLE_ID = "fellesen-theme-aliases";

function injectActionAliases(lightShade: Shade, darkShade: Shade): void {
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement("style");
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent =
    `:root{--primary:var(--primary-${lightShade});--primary-fg:255 255 255}` +
    `[data-theme="dark"]{--primary:var(--primary-${darkShade});--primary-fg:var(--primary-950)}`;
}

function assertContrast(mode: "light" | "dark", bg: Rgb, fg: Rgb): void {
  const ratio = contrastRatio(bg, fg);
  if (ratio < 4.5) {
    console.warn(
      `[theme] primary action (${mode}) = ${ratio.toFixed(2)}:1 — below WCAG AA 4.5:1. Adjust theme.config.ts.`,
    );
  }
}
